import type { Bounds, Filters } from './consts';

export type AlertLocationCriteria =
  | { location_mode: 'named'; location_name: string; bounds: null }
  | { location_mode: 'viewport'; location_name: null; bounds: Bounds }
  | { location_mode: 'all'; location_name: null; bounds: null };

export type AlertCriteriaSummary = {
  location_mode: 'named' | 'viewport' | 'all';
  location_name: string | null;
  bounds: Bounds | null;
  max_price_idr: number | null;
  min_bedrooms: number | null;
  keywords: string[];
  display_currency?: string;
};

export type CreateAlertCriteria = AlertLocationCriteria & {
  email: string;
  max_price_idr: number | null;
  min_bedrooms: number | null;
  keywords: string[];
  display_currency: string;
};

export function buildAlertCriteria(
  email: string,
  filters: Filters,
  selectedLocation: string | null,
  bounds: Bounds | null,
  displayCurrency = 'IDR',
): CreateAlertCriteria {
  const locationCriteria: AlertLocationCriteria = selectedLocation
    ? { location_mode: 'named', location_name: selectedLocation, bounds: null }
    : bounds
      ? { location_mode: 'viewport', location_name: null, bounds }
      : { location_mode: 'all', location_name: null, bounds: null };

  return {
    ...locationCriteria,
    email,
    max_price_idr: filters.price_range && filters.price_range !== 'Infinity'
      ? Number(filters.price_range) * 1_000_000
      : null,
    min_bedrooms: filters.min_bedrooms ? Number(filters.min_bedrooms) : null,
    keywords: filters.keywords.replace(/,/g, ' ').split(/\s+/).map(token => token.trim().toLowerCase()).filter(Boolean),
    display_currency: displayCurrency,
  };
}

export function alertCriteriaRows(criteria: AlertCriteriaSummary): { label: string; value: string }[] {
  const location = criteria.location_mode === 'named'
    ? criteria.location_name || 'Selected location'
    : criteria.location_mode === 'viewport'
      ? 'Current map area'
      : 'All Bali listings';

  return [
    { label: 'Location', value: location },
    {
      label: 'Price',
      value: criteria.max_price_idr ? `Up to ${criteria.max_price_idr / 1_000_000}M IDR / month` : 'Any price',
    },
    {
      label: 'Bedrooms',
      value: criteria.min_bedrooms ? `${criteria.min_bedrooms}+ bedrooms` : 'Any bedrooms',
    },
    {
      label: 'Keywords',
      value: criteria.keywords.length ? criteria.keywords.join(', ') : 'No keywords',
    },
  ];
}

export function isBroadAlert(criteria: AlertCriteriaSummary): boolean {
  return criteria.max_price_idr === null
    && criteria.min_bedrooms === null
    && criteria.keywords.length === 0;
}

export function describeAlertCriteria(criteria: AlertCriteriaSummary): string {
  return alertCriteriaRows(criteria).map(row => row.value).join(' · ');
}
